import * as React from 'react'
import { Logger, RequestContext } from '@utils'
import { PlayerCapabilities, ContentConfig } from 'ATVWebPlayer'
import { ClientsideLogger } from '../../util/logger/logger'
import { createPlayer, PlayerProxy, HistoryItem } from './integration'
import * as playerConfig from './integration/config'
import { sendStartPlaybackAnalytics } from './integration/logger'
import './player.module.scss'

export { playerConfig }

if (!global.__SERVER__) {
  require('./integration/polyfill')
}

const ERROR_PLAYER_NOT_READY = 'DVWebPlayer:notReady'
const ERROR_PLAYBACK_ABORTED = 'DVWebPlayer:aborted'
const ERROR_PLAYBACK_MISSING_DRM = 'DVWebPlayer:missingDrm'
const LEGACY_PLAYBACK = 'DVWebPlayer:legacyPlayback'

const LEGACY_DEEPLINK_CLASS = '.js-deeplinkable'
const LEGACY_ATTR_SIGNIN_URL = 'signInUrl'
const LEGACY_ATTR_HREF = 'href'
const LEGACY_ATTR_LCID = 'data-lcid'
const LEGACY_ATTR_ASIN = 'data-asin'
const LEGACY_ATTR_TITLE_ID = 'data-title-id'
const LEGACY_ATTR_RESUME_TIME = 'data-resume-time'
const LEGACY_ATTR_IS_TRAILER = 'isTrailer'
const LEGACY_ATTR_VIDEO_TYPE = 'data-video-type'
const LEGACY_ATTR_CLICKSTREAM_REF = 'data-clickstream-ref'
const LEGACY_ATTR_REF = 'data-ref'

export type PlayerContentConfig = ContentConfig & {
  lcid?: string
  ref?: string
}

export type PlaybackContentLink = {
  contentConfig: PlayerContentConfig
  signInUrl?: string
  playbackUrl: string
}

export type PlayerIntegrationProps = {
  os?: string
  logger?: Logger
  playerConfig: playerConfig.PlayerConfigData
  playbackLinkData: PlaybackContentLink | undefined
  whisperCacheData: PlayerContentConfig | undefined
  onAsinChanged?: (config: HistoryItem) => unknown
  onPlay?: (config: PlayerContentConfig) => unknown
  onNavigateToPlay?: (config: PlayerContentConfig) => void
  onPlayFailure?: () => void
  onClosed?: (config: HistoryItem) => unknown
  onInitialized?: () => void
  onPrepare?: () => void
  onPlayerCapabilities?: (capabilities: PlayerCapabilities) => void
  liveIncompatibleCallback: () => void
}

type Overflow = 'hidden' | 'visible'

const setBodyOverflow = (overflow: Overflow) => {
  document.getElementsByTagName('body')[0].style.overflow = overflow
}

const setPlayerClass = (className: string) => {
  document.getElementById(playerConfig.PLAYER_ELEMENT_ID)!.className = className
}

const showVideo = (_content: PlayerContentConfig) => {
  setPlayerClass('dv-player-fullscreen')
  setBodyOverflow('hidden')
}

const hideVideo = (_content: PlayerContentConfig) => {
  setPlayerClass('')
  setBodyOverflow('visible')
}

export const navigate = (url: string) => {
  window.location.assign(url)
}

export class PlayerIntegration extends React.Component<PlayerIntegrationProps> {
  private readonly logger: Logger
  private player?: PlayerProxy

  constructor(props: PlayerIntegrationProps) {
    super(props)
    this.logger =
      props.logger ||
      new ClientsideLogger(
        `DVWebNode:Player:${this.props.playerConfig.pageType}`
      )
  }

  logFatal(error: string) {
    this.count(error).fatal(error)
  }

  count(counter: string) {
    const { pageType } = this.props.playerConfig
    return this.logger.count(`${counter}:${pageType}`, 1, true)
  }

  initializeLegacyPlaybackSupport() {
    //used for Hover old and new
    document.addEventListener('click', event => {
      const element =
        event && (event.target as Element).closest(LEGACY_DEEPLINK_CLASS)
      if (element) {
        const attr = (attr: string) => element.getAttribute(attr)
        const parentAttr = (attr: string) =>
          element.parentElement && element.parentElement.getAttribute(attr)
        const signInUrl =
          attr(LEGACY_ATTR_SIGNIN_URL) ||
          parentAttr(LEGACY_ATTR_SIGNIN_URL) ||
          undefined
        const playbackUrl = attr(LEGACY_ATTR_HREF)
        const lcid = attr(LEGACY_ATTR_LCID) || undefined
        const asin = attr(LEGACY_ATTR_ASIN) || attr(LEGACY_ATTR_TITLE_ID)!
        const position = parseInt(attr(LEGACY_ATTR_RESUME_TIME) || '0')
        const trailer = Boolean(attr(LEGACY_ATTR_IS_TRAILER))
        const videoType = attr(LEGACY_ATTR_VIDEO_TYPE)
        const clickstreamRefMarker =
          attr(LEGACY_ATTR_CLICKSTREAM_REF) ||
          attr(LEGACY_ATTR_REF) ||
          undefined

        if (playbackUrl && (asin || lcid) && videoType) {
          const contentConfig: PlayerContentConfig = {
            asin,
            trailer,
            position,
            ref: clickstreamRefMarker,
            videoType,
            lcid
          }

          this.count(LEGACY_PLAYBACK)
          this.handlePlayRequest({ contentConfig, signInUrl, playbackUrl })
          event.preventDefault()
        }
      }
    })
  }

  async attachRefreshEventHandlers() {
    const {
      onPlay,
      onClosed,
      onAsinChanged,
      onInitialized,
      onPrepare,
      onPlayerCapabilities
    } = this.props

    const player = this.player!

    player.on('play', showVideo)
    onPlay && player.on('play', onPlay)
    player.on('closed', hideVideo)
    onClosed && player.on('closed', onClosed)
    onAsinChanged && player.on('asinChanged', onAsinChanged)
    onInitialized && player.on('initialized', onInitialized)
    onPrepare && player.on('prepare', onPrepare)

    player.on('aborted', () => {
      this.count(ERROR_PLAYBACK_ABORTED)
    })

    const capabilities = await player.capabilities
    if (!capabilities.supportsDrm) {
      this.count(ERROR_PLAYBACK_MISSING_DRM)
    }
    onPlayerCapabilities && onPlayerCapabilities(capabilities)
  }

  async componentDidMount() {
    const {
      playerConfig,
      whisperCacheData,
      liveIncompatibleCallback
    } = this.props
    try {
      this.player = await createPlayer(playerConfig, liveIncompatibleCallback)
      this.attachRefreshEventHandlers()
      this.initializeLegacyPlaybackSupport()
      if (whisperCacheData) {
        await this.handlePrepareRequest(whisperCacheData)
      }
    } catch (error) {
      this.logFatal(error)
    }
  }

  componentDidUpdate({
    playbackLinkData: previousLinkData,
    whisperCacheData: previousWhisperCacheData
  }: PlayerIntegrationProps) {
    const { playbackLinkData, whisperCacheData } = this.props
    if (playbackLinkData && !previousLinkData) {
      this.handlePlayRequest(playbackLinkData)
    }

    if (
      !playbackLinkData &&
      whisperCacheData &&
      whisperCacheData !== previousWhisperCacheData
    ) {
      this.handlePrepareRequest(whisperCacheData)
    }
  }

  async componentWillUnmount() {
    this.player && this.player.resetPlayer()
  }

  async handlePrepareRequest(contentConfig: PlayerContentConfig) {
    if (this.player && this.isPlaybackSupported(contentConfig)) {
      this.player.prepare(contentConfig)
    }
  }

  async handlePlayRequest({
    contentConfig,
    signInUrl,
    playbackUrl
  }: PlaybackContentLink) {
    const { onNavigateToPlay, onPlayFailure } = this.props
    const { ref } = contentConfig

    if (ref) {
      sendStartPlaybackAnalytics(ref)
    }

    let playSuccess = true

    if (!this.player) {
      this.logFatal(ERROR_PLAYER_NOT_READY)
      playSuccess = false
    } else if (!this.isPlaybackSupported(contentConfig)) {
      navigate(playbackUrl)

      if (onNavigateToPlay) {
        onNavigateToPlay(contentConfig)
      }
    } else if (signInUrl) {
      navigate(signInUrl)
    } else {
      try {
        await this.player.play(contentConfig)
      } catch (e) {
        playSuccess = false
      }
    }

    if (!playSuccess && onPlayFailure) {
      onPlayFailure()
    }
  }

  isPlaybackSupported(content: PlayerContentConfig) {
    return (
      (content.videoType || '').toLowerCase() === 'trailer' ||
      (this.props.os || '').toLowerCase() !== 'android'
    )
  }

  render(): null {
    return null
  }
}

type SimplePlayerProps = {
  requestContext: RequestContext,
  pageType: playerConfig.PageType
}

export const SimplePlayer = ({
  requestContext,
  pageType
}: SimplePlayerProps) => {
  const { sessionID, marketplaceID, customerID, locale } = requestContext
  const playerConfig = {
    sessionId: sessionID,
    marketplaceId: marketplaceID,
    customerId: customerID,
    locale,
    playerBeta: false,
    useDefaultView: true,
    pageType
  }
  return (
    <PlayerIntegration
      playerConfig={playerConfig}
      playbackLinkData={undefined}
      whisperCacheData={undefined}
      liveIncompatibleCallback={undefined}
    />
  )
}
